import React from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'
import { injectIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'

import AppDispatcher from '../../dispatchers/AppDispatcher'

import NavigationBar from '../partials/NavigationBar'
import RuleDetail from '../partials/RuleComponents/RuleDetail'
import Footer from '../partials/Footer'
import { CapitalButton } from '../partials/Navigation/Buttons'
import withAppStore from '../../util/withAppStore'

import pageStyles from '../../../css/devicePage.module.css'

function Rule (props) {
  const navigate = useNavigate()
  const { deviceID, ruleID, customerID } = useParams()

  const [showSaveButton, setShowSaveButton] = React.useState(false)

  const getNewData = React.useCallback(() => {
    if (ruleID !== 'new') {
      AppDispatcher.dispatch({ action: 'get-rule', ruleID })
    }
    AppDispatcher.dispatch({ action: 'get-device', deviceID })
  }, [deviceID, ruleID])

  const backToDevice = React.useCallback(() => {
    navigate(`/${customerID}/devices/${deviceID}/rules`)
  }, [customerID, deviceID, navigate])

  const deleteRule = React.useCallback(() => {
    const {
      data: {
        lookup,
        rules: {
          rule,
        },
      },
    } = props

    if (ruleID === 'new') {
      backToDevice()
    } else {
      const newRule = rule
      // active must be a string for the server
      newRule.active = 'false'
      AppDispatcher.dispatch({
        action: 'update-new-rule-basic',
        address: lookup[deviceID].address,
        active: 'false',
        nextNotification: '0',
      })
      saveToServer()
    }
  }, [ruleID, backToDevice, props.data?.lookup, props.data?.rules?.rule, deviceID])

  const saveToServer = React.useCallback(() => {
    const {
      data: {
        rules: {
          newRule,
        },
      },
    } = props

    if (ruleID === 'new') {
      AppDispatcher.dispatch({
        action: 'set-rule',
        ruleID: null,
        rule: newRule,
        update: false,
      })
    } else {
      AppDispatcher.dispatch({
        action: 'set-rule',
        ruleID,
        rule: newRule,
        update: true,
      })
    }
  }, [ruleID, props.data?.rules?.newRule])

  const setShowSaveButtonCallback = React.useCallback((value = true) => {
    setShowSaveButton(value)
  }, [])

  React.useEffect(() => {
    getNewData()
  }, [getNewData])

  React.useEffect(() => {
    return () => {
      AppDispatcher.dispatch({
        action: 'unload-rule',
      })
    }
  }, [])

  React.useEffect(() => {
    const { data: { rules: { saved } = {} } = {} } = props

    if (saved === true) {
      setTimeout(() => {
        backToDevice()
      }, 200)
    }
  }, [props.data?.rules?.saved, backToDevice])

  const {
    auth: {
      logout,
    },
    data: {
      lookup,
      lookupReady,
      rules: {
        invalid,
        invalidContactInfo,
        rule,
        ruleReady,
        saved,
      },
    },
    intl: { formatMessage },
    pages: {
      Alert,
    },
    route: {
      path,
    },
  } = props

  let ruleDetail

  if (lookupReady && ruleID === 'new') {
    const device = lookup[deviceID]
    const { address } = device

    ruleDetail = (
      <RuleDetail
        address={address}
        device={device}
        rule={null}
        ruleID={ruleID}
        saveButtonCallback={setShowSaveButtonCallback}
      />
    )
  } else if (lookupReady && ruleReady) {
    const device = lookup[deviceID]
    const { address } = device

    ruleDetail = (
      <RuleDetail
        address={address}
        device={device}
        rule={rule}
        ruleID={ruleID}
        saveButtonCallback={setShowSaveButtonCallback}
      />
    )
  }

  let saveButtonStyle = `${pageStyles.deselectAllButton} saveButton`

  if (saved === true) {
    saveButtonStyle += ` ${pageStyles.saveButtonSuccess}`
  } else if (saved === 'error') {
    saveButtonStyle += ` ${pageStyles.saveButtonError}`
  }

  const saveButton = (
    <div hidden={!showSaveButton}>
      <CapitalButton
        buttonFunction={saveToServer}
        disabled={invalid || invalidContactInfo}
        hSize='h2'
        style={saveButtonStyle}
        text={formatMessage({
          id: 'setting.save',
          defaultMessage: 'SAVE',
          description: 'SAVE button',
        })}
      />
    </div>
  )

  return (
    <div name='Rule'>
      <Helmet><title>Alert</title></Helmet>
      <NavigationBar
        page={path}
        backFunc={backToDevice}
        name={formatMessage({
          id: 'setting.alertRule.heading',
          defaultMessage: 'Alert Rule',
          description: 'Alert Rule heading',
        })}
        rightControl={{
          icon: 'fa-trash-o',
          func: deleteRule,
        }}
        logOut={() => logout(navigate)}
      />
      <div className={pageStyles.pageWidth}>
        <div className={pageStyles.pageContainer}>
          {ruleDetail}
          <section>
            {saveButton}
          </section>
        </div>
      </div>
      <Footer />
    </div>
  )
}

Rule.propTypes = {
  route: PropTypes.object,
  pages: PropTypes.object,
  data: PropTypes.object,
  auth: PropTypes.object,
  intl: PropTypes.object,
}

export default withAppStore(injectIntl(Rule))
